import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'

type CursorState = 'default' | 'hover' | 'label' | 'hidden'

/**
 * Soft circular cursor for mouse users. It grows over links and shows a
 * label over anything marked with data-cursor="View" (or "Drag", "Play").
 */
export const Cursor: FC = (): ReactNode => {
    const [enabled, setEnabled] = useState(false)
    const dotRef = useRef<HTMLDivElement>(null)
    const labelRef = useRef<HTMLDivElement>(null)
    const textRef = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        const fine = window.matchMedia('(hover: hover) and (pointer: fine)')
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
        const update = () => setEnabled(fine.matches && !reduced.matches)

        update()
        fine.addEventListener('change', update)
        reduced.addEventListener('change', update)

        return () => {
            fine.removeEventListener('change', update)
            reduced.removeEventListener('change', update)
        }
    }, [])

    useEffect(() => {
        const dot = dotRef.current
        const label = labelRef.current
        const text = textRef.current

        if (!enabled || !dot || !label || !text) return

        const root = document.documentElement

        let x = -200
        let y = -200
        let cx = -200
        let cy = -200
        let raf = 0
        let visible = false

        const setState = (state: CursorState) => {
            dot.dataset.state = state
            label.dataset.state = state
        }

        const setVisible = (value: boolean) => {
            visible = value
            dot.dataset.visible = String(value)
            label.dataset.visible = String(value)
        }

        const tick = () => {
            cx += (x - cx) * 0.3
            cy += (y - cy) * 0.3

            const transform = `translate3d(${cx.toFixed(1)}px, ${cy.toFixed(1)}px, 0)`

            dot.style.transform = transform
            label.style.transform = transform

            raf = Math.abs(x - cx) > 0.2 || Math.abs(y - cy) > 0.2
                ? window.requestAnimationFrame(tick)
                : 0
        }

        const onMove = (event: PointerEvent) => {
            if (event.pointerType !== 'mouse') return

            x = event.clientX
            y = event.clientY

            if (!visible) {
                cx = x
                cy = y
                setVisible(true)
            }

            if (!raf) raf = window.requestAnimationFrame(tick)
        }

        const onOver = (event: MouseEvent) => {
            const target = event.target instanceof Element ? event.target : null

            if (!target) return

            const labelled = target.closest<HTMLElement>('[data-cursor]')
            const typing = target.closest('input:not([type="checkbox"]):not([type="radio"]):not([type="submit"]), textarea, select, [contenteditable="true"]')
            const interactive = target.closest('a, button, [role="button"], label, summary, [data-cursor-hover]')

            if (typing) {
                setState('hidden')
            }
            else if (labelled?.dataset.cursor) {
                text.textContent = labelled.dataset.cursor
                setState('label')
            }
            else if (interactive) {
                setState('hover')
            }
            else {
                setState('default')
            }
        }

        const onLeave = () => setVisible(false)
        const onDown = () => { dot.dataset.pressed = 'true' }
        const onUp = () => { dot.dataset.pressed = 'false' }

        root.classList.add('has-cursor')
        window.addEventListener('pointermove', onMove, { passive: true })
        window.addEventListener('pointerdown', onDown)
        window.addEventListener('pointerup', onUp)
        document.addEventListener('mouseover', onOver)
        root.addEventListener('mouseleave', onLeave)

        return () => {
            root.classList.remove('has-cursor')
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerdown', onDown)
            window.removeEventListener('pointerup', onUp)
            document.removeEventListener('mouseover', onOver)
            root.removeEventListener('mouseleave', onLeave)
            window.cancelAnimationFrame(raf)
        }
    }, [enabled])

    if (!enabled) return null

    return (
        <>
            <div
                ref={dotRef}
                aria-hidden='true'
                data-state='default'
                data-visible='false'
                className='group/dot pointer-events-none fixed left-0 top-0 z-[130] mix-blend-difference'
            >
                <span className='block size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white opacity-100 transition-[scale,opacity] duration-300 ease-expo group-data-[pressed=true]/dot:scale-75 group-data-[state=hidden]/dot:scale-0 group-data-[state=hover]/dot:scale-[2.9] group-data-[state=label]/dot:scale-0 group-data-[visible=false]/dot:opacity-0' />
            </div>

            <div
                ref={labelRef}
                aria-hidden='true'
                data-state='default'
                data-visible='false'
                className='group/label pointer-events-none fixed left-0 top-0 z-[131]'
            >
                <span className='flex size-[86px] -translate-x-1/2 -translate-y-1/2 scale-0 items-center justify-center rounded-full bg-ember font-mono text-[11px] uppercase tracking-[0.14em] text-ink shadow-[0_12px_30px_-10px_rgb(255_92_0/0.7)] transition-[scale,opacity] duration-500 ease-expo group-data-[state=label]/label:scale-100 group-data-[visible=false]/label:opacity-0'>
                    <span ref={textRef}>
                        View
                    </span>
                </span>
            </div>
        </>
    )
}
